import { useCallback, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { DateTime } from 'luxon';
import { useMeContext } from 'modules/me';
import { useSmartTimesheetContext } from 'modules/smartDiary/smartDiaryNotes/SmartTimesheetContextProvider';
import { Period } from 'graphql/types';
import { SmartDiaryEntry } from '../../type';
import { getDaysOfPeriod } from '../../hooks';
import { SyncError } from './useApplyToTimsheet';

const DEFAULT_TTL_IN_SEC = 60 * 86400; // 60 days

export interface UseSmartDiaryLastSyncErrorsProps {
  ttlInSeconds?: number;
}

export interface UseSmartDiaryLastSyncErrorsResultProps {
  lastSyncErrors: SyncError[];
  updateLastSyncErrors: (
    smartEntries: SmartDiaryEntry[],
    errors: Record<string, SyncError[]>
  ) => void;
}

interface GroupErrorsProps {
  smartEntries: SmartDiaryEntry[];
  errors: Record<string, SyncError[]>;
  currentPeriod: Period | null;
}

interface PopulateLastSyncErrorsProps {
  groupedErrors: Map<string, SyncError[]>;
  lastSyncErrors: Map<string, { syncErrors: SyncError[]; ttl: number }>;
  currentPeriod: Period | null;
  currentTimeInSeconds: number;
}

export const groupErrors = ({
  smartEntries,
  errors,
  currentPeriod
}: GroupErrorsProps): Map<string, SyncError[]> => {
  const groupedErrors = new Map<string, SyncError[]>();

  Object.values(errors)
    .flat()
    .forEach((value: SyncError) => {
      const associatedEntry = smartEntries.find(
        (entry: SmartDiaryEntry) => entry.id === value.id
      );

      if (associatedEntry) {
        const currentGroup = groupedErrors.get(associatedEntry.date) ?? [];
        groupedErrors.set(associatedEntry.date, [...currentGroup, value]);
      } else {
        getDaysOfPeriod(currentPeriod).forEach((date: string) => {
          const currentGroup = groupedErrors.get(date) ?? [];
          groupedErrors.set(date, [...currentGroup, value]);
        });
      }
    });

  return groupedErrors;
};

export const populateLastSyncErrors = ({
  groupedErrors,
  lastSyncErrors,
  currentPeriod,
  currentTimeInSeconds
}: PopulateLastSyncErrorsProps): void => {
  getDaysOfPeriod(currentPeriod).forEach((date: string) => {
    const errorsForDate = groupedErrors.get(date);

    if (errorsForDate) {
      lastSyncErrors!.set(date, {
        syncErrors: errorsForDate,
        ttl: currentTimeInSeconds
      });
    } else {
      lastSyncErrors!.delete(date);
    }
  });
};

export const useSmartDiaryLastSyncErrors = ({
  ttlInSeconds = DEFAULT_TTL_IN_SEC
}: UseSmartDiaryLastSyncErrorsProps = {}): UseSmartDiaryLastSyncErrorsResultProps => {
  const me = useMeContext();
  const currentDate = DateTime.local();
  const { currentPeriod } = useSmartTimesheetContext();
  const [lastSyncErrors, setLastSyncErrors] = useLocalStorage<
    Map<string, { syncErrors: SyncError[]; ttl: number }>
  >(`${me?.id}_lastSyncErrors`, new Map(), {
    raw: false,
    serializer: (
      value: Map<string, { syncErrors: SyncError[]; ttl: number }>
    ) => JSON.stringify(Array.from(value.entries())),
    deserializer: (value: string) => new Map(JSON.parse(value))
  });

  useEffect(() => {
    Array.from(lastSyncErrors!.entries()).forEach(([date, value]) => {
      const datetime = DateTime.fromSeconds(value.ttl);
      const diffInSeconds = currentDate.diff(datetime).milliseconds / 1000;
      if (diffInSeconds > ttlInSeconds) {
        lastSyncErrors!.delete(date);
      }
    });

    setLastSyncErrors(lastSyncErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLastSyncErrors = useCallback(
    (smartEntries: SmartDiaryEntry[], errors: Record<string, SyncError[]>) => {
      const groupedErrors = groupErrors({
        smartEntries,
        errors,
        currentPeriod
      });
      populateLastSyncErrors({
        groupedErrors,
        lastSyncErrors: lastSyncErrors!,
        currentPeriod,
        currentTimeInSeconds: DateTime.local().toSeconds()
      });
      setLastSyncErrors(lastSyncErrors);
    },
    [currentPeriod, setLastSyncErrors, lastSyncErrors]
  );

  const lastSyncErrorsForPeriod = useMemo(() => {
    const allErrors = getDaysOfPeriod(currentPeriod)
      .map((day: string) => lastSyncErrors!.get(day)?.syncErrors || [])
      .flat();

    const dedupedErrors = allErrors.filter((error, index, array) => {
      return (
        array.findIndex(
          (e: SyncError) =>
            e.id === error.id &&
            e.code === error.code &&
            e.description === error.description
        ) === index
      );
    });

    return dedupedErrors;
  }, [currentPeriod, lastSyncErrors]);

  return {
    lastSyncErrors: lastSyncErrorsForPeriod,
    updateLastSyncErrors
  };
};
